import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../lib/axios';
import { useLang, LanguageBar } from '../../i18n/cda';
import { CdaLogo } from '../../components/cda/CdaBrand';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  WORKFORCE_INTRO, WORKFORCE_MODULES, WORKFORCE_FINAL_MESSAGE, RESUME_TEMPLATES,
  READINESS_QUIZ, scoreToBand,
} from '../../data/workforceContent';
import { ArrowLeft, Check, X, ExternalLink, FileText, Award, Briefcase, ClipboardCheck, RefreshCw } from 'lucide-react';

const ModuleCard = ({ m, t }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid={`module-${m.id}`}>
    <div className="flex items-center gap-3 mb-1">
      <div className="w-9 h-9 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">{m.id}</div>
      <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t(m.title)}</h3>
    </div>
    <p className="text-[#c4622d] font-medium text-sm mb-1">{t(m.tagline)}</p>
    <p className="text-slate-600 text-sm mb-4">{t(m.intro)}</p>
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <ul className="space-y-1.5">
          {m.recommended.map((r) => (
            <li key={r} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#0d7d6f] shrink-0 mt-0.5" /> {t(r)}</li>
          ))}
        </ul>
      </div>
      {m.avoid.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-red-500 mb-1.5">{t('Avoid')}</p>
          <ul className="space-y-1.5">
            {m.avoid.map((a) => (
              <li key={a} className="flex items-start gap-2 text-sm text-slate-600"><X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" /> {t(a)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
    {m.id === 9 && (
      <div className="mt-4 grid sm:grid-cols-3 gap-2">
        {RESUME_TEMPLATES.map((tpl) => (
          <a key={tpl.url} href={tpl.url} target="_blank" rel="noopener noreferrer" data-testid={`resume-${tpl.name.replace(/\s+/g, '-').toLowerCase()}`}
            className="block p-3 bg-[#0d7d6f]/5 border border-[#0d7d6f]/20 rounded-lg hover:border-[#0d7d6f]/50">
            <div className="flex items-center justify-between"><span className="font-medium text-[#0d7d6f] text-sm">{t(tpl.name)}</span><ExternalLink className="w-3.5 h-3.5 text-slate-400" /></div>
            <p className="text-xs text-slate-500 mt-0.5">{t(tpl.note)}</p>
          </a>
        ))}
      </div>
    )}
  </div>
);

const ReadinessQuiz = ({ initialScore, initialBand, t }) => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(
    initialScore != null && initialBand ? { score: initialScore, band: scoreToBand(initialScore) } : null
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < READINESS_QUIZ.length) {
      toast.error(t('Please answer all 20 questions.'));
      return;
    }
    let correct = 0;
    READINESS_QUIZ.forEach((item, i) => { if (answers[i] === item.answer) correct += 1; });
    const score = correct * 5;
    const band = scoreToBand(score);
    setSubmitting(true);
    try {
      await axios.post('/cda/readiness-score', { score, band: band.id, answers });
      setResult({ score, band });
      window.scrollTo({ top: document.getElementById('readiness-result')?.offsetTop || 0, behavior: 'smooth' });
    } catch (e) {
      // still show locally even if save fails
      setResult({ score, band });
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => { setAnswers({}); setResult(null); };

  return (
    <div className="bg-white rounded-2xl border-2 border-[#0d7d6f]/20 p-6 sm:p-8" data-testid="readiness-quiz">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardCheck className="w-7 h-7 text-[#0d7d6f]" />
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('Professional Readiness Score')}</h2>
      </div>
      <p className="text-slate-600 mb-6">{t('Answer 20 quick questions to see how workplace-ready you are. Directors love candidates who are CDA-trained and workforce-ready.')}</p>

      {result ? (
        <div id="readiness-result" className="text-center py-6" data-testid="readiness-result">
          <div className="text-5xl mb-2">{result.band.emoji}</div>
          <div className="text-5xl font-bold mb-1" style={{ color: result.band.color, fontFamily: 'Manrope, sans-serif' }}>{result.score}/100</div>
          <p className="text-xl font-semibold mb-2" style={{ color: result.band.color }}>{t(result.band.label)}</p>
          <p className="text-slate-600 max-w-md mx-auto mb-6">{t(result.band.note)}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={retake} variant="outline" data-testid="readiness-retake"><RefreshCw className="w-4 h-4 mr-2" /> {t('Retake')}</Button>
            <Button onClick={() => navigate('/cda/journey')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">{t('Back to my journey')}</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-5">
            {READINESS_QUIZ.map((item, i) => (
              <div key={i} data-testid={`quiz-q-${i}`}>
                <p className="font-medium text-slate-800 mb-2"><span className="text-[#0d7d6f] font-bold">{i + 1}.</span> {t(item.q)}</p>
                <div className="space-y-1.5">
                  {item.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers({ ...answers, [i]: oi })}
                      data-testid={`quiz-${i}-opt-${oi}`}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all ${answers[i] === oi ? 'border-[#0d7d6f] bg-[#0d7d6f]/5 text-slate-900 font-medium' : 'border-slate-200 text-slate-600 hover:border-[#0d7d6f]/40'}`}>
                      {t(opt)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={submit} disabled={submitting} className="mt-6 w-full h-12 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358] text-base" data-testid="readiness-submit">
            {submitting ? t('Scoring…') : t('Get my Readiness Score')}
          </Button>
          <p className="text-xs text-center text-slate-400 mt-2">{Object.keys(answers).length}/20 {t('answered')}</p>
        </>
      )}
    </div>
  );
};

const WorkforceReadiness = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [progress, setProgress] = useState(null);

  React.useEffect(() => {
    axios.get('/cda/progress').then((r) => setProgress(r.data)).catch(() => setProgress({}));
  }, []);

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <LanguageBar />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <CdaLogo size={34} />
          <button onClick={() => navigate('/cda/journey')} className="flex items-center gap-1.5 text-sm text-[#0d7d6f] hover:underline" data-testid="workforce-back">
            <ArrowLeft className="w-4 h-4" /> {t('Back')}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0d7d6f]/10 text-[#0d7d6f] text-sm font-medium mb-4">
            <Briefcase className="w-4 h-4" /> {t('Workforce Readiness')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('Get ready for the job — not just the credential')}
          </h1>
          <p className="text-slate-600">{t(WORKFORCE_INTRO)}</p>
        </div>

        {WORKFORCE_MODULES.map((m) => <ModuleCard key={m.id} m={m} t={t} />)}

        {/* Final message */}
        <div className="bg-gradient-to-br from-[#0d7d6f] to-[#0a6358] rounded-2xl p-6 text-white text-center">
          <Award className="w-8 h-8 mx-auto mb-2" />
          <p className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{t(WORKFORCE_FINAL_MESSAGE)}</p>
        </div>

        {/* Quiz */}
        <ReadinessQuiz initialScore={progress?.readiness_score} initialBand={progress?.readiness_band} t={t} />
      </main>

      <footer className="py-8 text-center text-sm text-slate-500">
        {t('Care Comply is a service of Barro Roots Contracting. • CDA® is a trademark of the Council for Professional Recognition.')}
      </footer>
    </div>
  );
};

export default WorkforceReadiness;
